import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import * as S from './store.js';

const Ctx = createContext(null);

export function StoreProvider({ children }) {
  const [store, setStore] = useState(() => S.load());
  const [parentUnlocked, setParentUnlocked] = useState(false);

  const commit = useCallback((next) => {
    setStore((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      S.save(value);
      return value;
    });
  }, []);

  const api = useMemo(() => ({
    store,
    active: store.profiles.find((p) => p.id === store.activeId) ?? null,
    parentUnlocked,
    unlockParent: (pin) => {
      const ok = !store.parentPin || pin === store.parentPin;
      if (ok) setParentUnlocked(true);
      return ok;
    },
    lockParent: () => setParentUnlocked(false),
    addProfile: (fields) => commit((s) => S.addProfile(s, fields)),
    setActive: (id) => commit((s) => S.setActive(s, id)),
    setPin: (pin) => commit((s) => S.setPin(s, pin)),
    appendEvent: (profileId, event) => commit((s) => S.appendEvent(s, profileId, event)),
    introduceSkill: (profileId, skillId) => commit((s) => S.introduceSkill(s, profileId, skillId)),
    replaceStore: (next) => commit(next),
    toggleEnabled: (profileId, skillId, on) => commit((s) => S.toggleEnabled(s, profileId, skillId, on)),
    enableMany: (profileId, skillIds) => commit((s) => S.enableMany(s, profileId, skillIds)),
    toggleAdultEnabled: (profileId, skillId, on) => commit((s) => S.toggleAdultEnabled(s, profileId, skillId, on)),
    setPrivilege: (profileId, skillId, text) => commit((s) => S.setPrivilege(s, profileId, skillId, text)),
    renameProfile: (profileId, fields) => commit((s) => S.renameProfile(s, profileId, fields)),
    removeProfile: (profileId) => commit((s) => S.removeProfile(s, profileId)),
  }), [store, parentUnlocked, commit]);

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useStore() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useStore outside StoreProvider');
  return ctx;
}
